import { del, get, patch, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  BrandingConfig as BrandingConfigOpenApi,
  PageConfig as PageConfigOpenApi,
  UiBrandingCreateSchema as UiBrandingCreateSchemaOpenApi,
  UiBrandingGetSchema as UiBrandingGetSchemaOpenApi,
  UiBrandingUpdateSchema as UiBrandingUpdateSchemaOpenApi,
  UiBrandingWithOrgSchema as UiBrandingWithOrgSchemaOpenApi,
} from "@:openapi";

// Page key type for branding configuration
export type PageKey = "docs" | "audio" | "chat" | "analytics" | "translation";

// Re-export camel-cased types for consumers
export type PageConfig = CamelCasedPropertiesDeep<PageConfigOpenApi>;
export type BrandingConfig = CamelCasedPropertiesDeep<BrandingConfigOpenApi>;
export type UiBrandingGetSchema =
  CamelCasedPropertiesDeep<UiBrandingGetSchemaOpenApi>;
export type UiBrandingWithOrgSchema =
  CamelCasedPropertiesDeep<UiBrandingWithOrgSchemaOpenApi>;
export type UiBrandingCreateSchema =
  CamelCasedPropertiesDeep<UiBrandingCreateSchemaOpenApi>;
export type UiBrandingUpdateSchema =
  CamelCasedPropertiesDeep<UiBrandingUpdateSchemaOpenApi>;

export interface UiBranding {
  GET_ALL: {
    Response: UiBrandingWithOrgSchema[];
  };
  GET_BY_ID: {
    RequestPath: { id: string };
    Response: UiBrandingGetSchema;
  };
  GET_BY_ORG: {
    RequestPath: { organizationId: string };
    Response: UiBrandingGetSchema;
  };
  GET_BY_BOT: {
    RequestPath: { botId: string };
    Response: UiBrandingGetSchema;
  };
  POST: {
    RequestBody: UiBrandingCreateSchema;
    Response: UiBrandingGetSchema;
  };
  PATCH: {
    RequestBody: UiBrandingUpdateSchema;
    RequestPath: { id: string };
    Response: UiBrandingGetSchema;
  };
  DELETE: {
    RequestPath: { id: string };
    Response: boolean;
  };
}

export const uiBrandingQueryKey = createQueryKeys("uiBranding", {
  all: (enabled: boolean) => [{ enabled }],
  byId: (id: string) => [id],
  byOrganization: (organizationId: string) => [organizationId],
  byBot: (botId: string) => [botId],
});

export const useQueryGetAllUiBrandings = (
  enabled: boolean = true,
  options?: Omit<
    UseQueryOptions<UiBranding["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiBranding["GET_ALL"]["Response"], AxiosError>({
    queryKey: uiBrandingQueryKey.all(enabled).queryKey,
    queryFn: () =>
      get<UiBranding["GET_ALL"]["Response"]>(
        `/api/ui_branding?enabled=${enabled}`,
      ),
    ...options,
  });
};

export const useQueryGetUiBrandingById = (
  params: UiBranding["GET_BY_ID"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<UiBranding["GET_BY_ID"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiBranding["GET_BY_ID"]["Response"], AxiosError>({
    queryKey: uiBrandingQueryKey.byId(params.id).queryKey,
    queryFn: () =>
      get<UiBranding["GET_BY_ID"]["Response"]>(`/api/ui_branding/${params.id}`),
    ...options,
  });
};

export const useQueryGetUiBrandingByOrganization = (
  params: UiBranding["GET_BY_ORG"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<UiBranding["GET_BY_ORG"]["Response"] | null, AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiBranding["GET_BY_ORG"]["Response"] | null, AxiosError>({
    queryKey: uiBrandingQueryKey.byOrganization(params.organizationId).queryKey,
    queryFn: async () => {
      try {
        return await get<UiBranding["GET_BY_ORG"]["Response"]>(
          `/api/ui_branding/organization/${params.organizationId}`,
        );
      } catch (error) {
        if ((error as AxiosError).response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    ...options,
  });
};

export const useQueryGetUiBrandingByBot = (
  params: UiBranding["GET_BY_BOT"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<UiBranding["GET_BY_BOT"]["Response"] | null, AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<UiBranding["GET_BY_BOT"]["Response"] | null, AxiosError>({
    queryKey: uiBrandingQueryKey.byBot(params.botId).queryKey,
    queryFn: async () => {
      try {
        return await get<UiBranding["GET_BY_BOT"]["Response"]>(
          `/api/ui_branding/bot/${params.botId}`,
        );
      } catch (error) {
        if ((error as AxiosError).response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    ...options,
  });
};

export const useMutationCreateUiBranding = (
  options?: UseMutationOptions<
    UiBranding["POST"]["Response"],
    AxiosError,
    UiBranding["POST"]["RequestBody"]
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UiBranding["POST"]["RequestBody"]) => {
      return post<UiBranding["POST"]["Response"]>("/api/ui_branding", data);
    },
    onSuccess: (data) => {
      // Invalidate both active and inactive lists
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(true).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(false).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.byOrganization(data.organizationId)
          .queryKey,
      });
      if (data.botId) {
        queryClient.invalidateQueries({
          queryKey: uiBrandingQueryKey.byBot(data.botId).queryKey,
        });
      }
      options?.onSuccess?.(data, {} as any, {} as any);
    },
    onError: options?.onError,
  });
};

export const useMutationUpdateUiBranding = (
  options?: UseMutationOptions<
    UiBranding["PATCH"]["Response"],
    AxiosError,
    UiBranding["PATCH"]["RequestBody"] &
      UiBranding["PATCH"]["RequestPath"] & {
        organizationId: string;
        botId?: string | null;
      }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      data: UiBranding["PATCH"]["RequestBody"] &
        UiBranding["PATCH"]["RequestPath"] & {
          organizationId: string;
          botId?: string | null;
        },
    ) => {
      return patch<UiBranding["PATCH"]["Response"]>(
        `/api/ui_branding/${data.id}`,
        omit(data, ["id", "organizationId", "botId"]),
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate both active and inactive lists
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(true).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(false).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.byOrganization(variables.organizationId)
          .queryKey,
      });
      if (variables.botId) {
        queryClient.invalidateQueries({
          queryKey: uiBrandingQueryKey.byBot(variables.botId).queryKey,
        });
      }
      options?.onSuccess?.(data, variables, {} as any);
    },
    onError: options?.onError,
  });
};

export const useMutationDeleteUiBranding = (
  options?: UseMutationOptions<
    UiBranding["DELETE"]["Response"],
    AxiosError,
    UiBranding["DELETE"]["RequestPath"] & {
      organizationId: string;
      botId?: string | null;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: UiBranding["DELETE"]["RequestPath"] & {
        organizationId: string;
        botId?: string | null;
      },
    ) => {
      return del<UiBranding["DELETE"]["Response"]>(
        `/api/ui_branding/${params.id}`,
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate both active and inactive lists
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(true).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.all(false).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: uiBrandingQueryKey.byOrganization(variables.organizationId)
          .queryKey,
      });
      if (variables.botId) {
        queryClient.invalidateQueries({
          queryKey: uiBrandingQueryKey.byBot(variables.botId).queryKey,
        });
      }
      options?.onSuccess?.(data, variables, {} as any);
    },
    onError: options?.onError,
  });
};
